<template>
  <div class="relative flex aspect-[1] max-h-[735px] w-full flex-col-reverse md:flex-row mobile:aspect-auto mobile:max-h-full mobile:items-center">
    <SfScrollable
      v-if="$device.isDesktopOrTablet"
      ref="thumbsRef"
      class="mobile-thumbnail mr-4 w-full items-center [-ms-overflow-style:none] [scrollbar-width:none] mobile:hidden desktop:h-[735px] [&::-webkit-scrollbar]:hidden"
      direction="vertical"
      :active-index="activeIndex"
      :previous-disabled="activeIndex === 0"
      :next-disabled="activeIndex === images.length - 1"
      buttons-placement="floating"
    >
      <template #previousButton="defaultProps">
        <KikButton
          v-bind="defaultProps"
          :class="{ '!hidden': defaultProps.disabled }"
          class="absolute top-4 z-[1] rotate-90 !rounded-full bg-white mobile:hidden desktop:mr-4"
          variant="secondary"
          size="sm"
          square
          :aria-label="$t('scroll up')"
        >
          <SfIconChevronLeft size="sm" />
        </KikButton>
      </template>
      <button
        v-for="({ url, label }, index) in images"
        :key="`${label}-${index}-thumbnail`"
        type="button"
        :aria-label="label"
        :aria-current="activeIndex === index"
        class="relative -mb-2 w-full shrink-0 grow cursor-pointer snap-start border-b-4 border-b-transparent
        transition-colors focus-visible:outline focus-visible:outline-offset md:h-auto md:w-[80px] md:grow-0"
        :class="{ '!border-b-primary-700': activeIndex === index }"
        @mouseover="activeIndex = index;"
        @focus="activeIndex = index;"
      >
        <NuxtImg
          class="mb-1 border border-neutral-200"
          preset="pdpThumb"
          :alt="productGetters.getName(product)"
          width="80"
          height="80"
          :src="url"
        />
      </button>
      <template #nextButton="defaultProps">
        <KikButton
          v-bind="defaultProps"
          :class="{ '!hidden': defaultProps.disabled }"
          class="absolute bottom-4 z-[1] rotate-90 !rounded-full bg-white mobile:hidden desktop:mr-4"
          variant="secondary"
          size="sm"
          square
          :aria-label="$t('Scroll down')"
        >
          <SfIconChevronRight size="sm" />
        </KikButton>
      </template>
    </SfScrollable>
    <SfScrollable
      id="imgRef"
      ref="imgRef"
      class="pointer-events-auto h-[466px] w-[614px] snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] mobile:w-full
      tablet:h-[735px] desktop:h-[735px] desktop:overflow-x-hidden [&::-webkit-scrollbar]:hidden"
      :active-index="activeIndex"
      direction="horizontal"
      wrapper-class="h-full w-full"
      is-active-index-centered
      buttons-placement="none"
      tabindex="0"
      @on-scroll="updateActiveIndex"
    >
      <div
        v-for="({ url, label }, index) in images"
        :key="`${label}-${index}`"
        class="relative m-0 flex size-full shrink-0 grow basis-full snap-center justify-center p-0"
      >
        <div
          ref="touchRefs"
          class="pointer-events-auto flex size-full"
          @touchstart="onTouchstart"
          @touchmove="onTouchmove"
          @touchend="onTouchend"
        >
          <div
            ref="pinchRefs"
            class="size-full"
            :class="{ 'pointer-events-none': $device.isMobileOrTablet }"
            @touchend="onTouchend"
          >
            <ClientOnly>
              <PinchScrollZoom
                ref="zoomer"
                within
                centered
                :width="614"
                :scale="scale"
                :height="$device.isDesktopOrTablet ? 735 : 466"
                :min-scale="1"
                :max-scale="5"
                :wheel-velocity="$device.isDesktop ? 0 : 0.01"
                class="cursor-zoom-in mobile:!max-h-[466px] mobile:!w-full"
                @scaling="e => onEvent(e)"
                @wheel="handleWheelEvent"
                @mousemove="handleHoverEvent"
                @mouseleave="handleMouseLeaveEvent"
              >
                <div class="flex size-full justify-center object-contain">
                  <NuxtImg
                    preset="pdp"
                    :src="url"
                    :alt="productGetters.getName(product)"
                    :aria-label="label"
                    :loading="index === 0 ? 'eager' : 'lazy'"
                    class="mobile:m-auto "
                    draggable="false"
                  />
                </div>
              </PinchScrollZoom>
            </ClientOnly>
          </div>
        </div>
      </div>

      <div class="absolute left-0 top-0 z-[1]">
        <div
          v-if="newTag"
          class="relative mb-2 w-fit bg-neutral-900 px-4 uppercase text-white"
        >
          {{ $t('New') }}
        </div>
        <div
          v-if="onlineOnlyTag"
          class="relative mb-2 bg-neutral-900 px-4 uppercase text-white"
        >
          {{ $t('Online Only') }}
        </div>
        <div
          v-if="saleTag"
          class="relative mb-2 w-fit bg-primary-700 px-4 uppercase text-white"
        >
          -{{ saleTag }}%
          <span v-if="onlineSale">
            {{ $t('only Online') }}
          </span>
        </div>
      </div>
      <div
        v-if="marketingTags?.length"
        class="absolute bottom-0 left-0 z-[1] mb-2 mobile:mb-4"
      >
        <SharedMarketingTags
          :marketing-tags="marketingTags"
          pdp-or-plp="pdp"
        />
      </div>
      <KikButton
        v-if="$flags.hasnt('enabledKiosk') && $flags.has('shoppinglist.wishlist')"
        variant="tertiary"
        size="base"
        square
        class="absolute right-0 top-0 z-[2] mr-2 mt-2 !rounded-full hover:!bg-neutral-200 mobile:mt-2 tablet:hidden desktop:hidden"
        :aria-label="$t('Add to wishlist')"
        @click="handleToggleToWishlist"
      >
        <component
          :is="isInWishlist({ ...product, sku }) ? SfIconFavoriteFilled : SfIconFavorite"
          size="base"
          :class="isInWishlist({ ...product, sku }) ? 'fill-primary' : 'fill-neutral-500'"
        />
      </KikButton>
    </SfScrollable>
    <div class="absolute bottom-1/2 z-[3] hidden w-full justify-between gap-1 pt-0.5 mobile:visible mobile:flex">
      <KikButton
        class="ml-4 !rounded-full"
        variant="secondary"
        size="sm"
        square
        :class="{ invisible: activeIndex === 0 }"
        @click="setActiveIndex(activeIndex - 1)"
      >
        <SfIconChevronLeft size="sm" />
      </KikButton>
      <KikButton
        class="mr-4 !rounded-full"
        variant="secondary"
        size="sm"
        square
        :class="{ invisible: activeIndex === images.length - 1 }"
        @click="setActiveIndex(activeIndex + 1)"
      >
        <SfIconChevronRight size="sm" />
      </KikButton>
    </div>
    <div class="absolute bottom-0 mt-5 hidden w-full gap-1 bg-white pt-0.5 mobile:flex">
      <p
        v-for="({ }, i) in images"
        :key="`dot-${i}`"
        class="h-1 w-full bg-neutral-200"
        :class="{
          '!bg-primary-700': activeIndex === i,
        }"
        @click="setActiveIndex(i)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  SfScrollable,
  SfIconChevronRight,
  SfIconChevronLeft,
  SfIconFavoriteFilled,
  SfIconFavorite,
  type SfScrollableOnScrollData,
} from '@storefront-ui/vue';
import PinchScrollZoom, {
  type PinchScrollZoomEmitData,
  type PinchScrollZoomExposed,
} from '@coddicat/vue-pinch-scroll-zoom';
import '@coddicat/vue-pinch-scroll-zoom/style.css';
import { useProduct } from '~/composables/custom/useProduct/useProduct';
import { useWishlist } from '~/composables/useWishlist';
import { useUser } from '~/composables/custom/useUser/useUser';
import { productGetters } from '~/composables/getters';

interface ImageData {
  alt?: string;
  url: string;
  label?: string;
  mobile?: ImageData;
  desktop?: ImageData;
  dimensions?: {
    width?: number;
    height?: number;
  };
}

const pinchRefs = ref<HTMLElement[]>();
const touchRefs = ref<HTMLElement[]>();
const imgRef = ref<HTMLElement>();

const zoomer = ref<PinchScrollZoomExposed[]>();
const scale = ref(1);

const state = ref<PinchScrollZoomEmitData>({
  x: 0,
  y: 0,
  originX: 0,
  originY: 0,
  scale: 1,
  translateX: 0,
  translateY: 0,
});

const onEvent = (e: PinchScrollZoomEmitData): void => {
  state.value = e;
  scale.value = e.scale;
};

interface Props {
  images: ImageData[];
  saleTag?: number | undefined;
  newTag?: boolean;
  onlineOnlyTag?: boolean;
  onlineSale?: boolean;
  marketingTags?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  images: () => [],
  saleTag: undefined,
  newTag: false,
  onlineOnlyTag: false,
});

const route = useRoute();
const { product, sku } = useProduct(route.path);
const { isAuthenticated } = useUser();
const { toggleLoginDrawer } = useUiState();
const { isInWishlist, toggleToWishlist, addToQueue } = useWishlist();

const { $flags } = useNuxtApp();
const pinchDistance = ref(0);
const { images } = toRefs(props);
const scaling = ref(false);
const activeIndex = ref<number>(0);
const isNotSwipe = ref(true);

const setActiveIndex = (index: number) => {
  if (index < 0 || index > images.value.length) {
    return;
  }

  activeIndex.value = index;
  isNotSwipe.value = true;
};

const updateActiveIndex = (event: SfScrollableOnScrollData) => {
  if (isNotSwipe.value) {
    return;
  }

  const newActiveIndex = images.value.length - Math.floor(event.scrollWidth / (event.left + 1));

  activeIndex.value = newActiveIndex < 0 ? 0 : newActiveIndex;
};

const handleToggleToWishlist = () => {
  if (isAuthenticated.value) {
    toggleToWishlist({ ...product.value, sku: sku.value });
  }
  else {
    toggleLoginDrawer();
    addToQueue({ ...product.value, sku: sku.value });
  }
};

const onTouchstart = (event: TouchEvent) => {
  if (event.touches.length === 2) {
    event.preventDefault();
    scaling.value = true;
  }
};

const onTouchmove = (event: TouchEvent) => {
  if (!scaling.value) {
    isNotSwipe.value = false;

    return;
  }

  event.preventDefault();
  const dist = Math.hypot(
    event.touches[0].pageX - event.touches[1].pageX,
    event.touches[0].pageY - event.touches[1].pageY,
  );
  scale.value = Math.min(5, Math.max(scale.value + ((dist - (pinchDistance.value === 0 ? dist : pinchDistance.value)) / 100), 1));
  pinchDistance.value = dist;
};

const onTouchend = () => {
  scale.value = state.value.scale;

  if (scale.value === 1) {
    scaling.value = false;

    pinchRefs.value?.forEach((ref) => {
      ref.style.pointerEvents = 'none';
    });

    touchRefs.value?.forEach((ref) => {
      ref.style.pointerEvents = 'auto';
    });

    return;
  }

  pinchRefs.value?.forEach((ref) => {
    if (ref.style.pointerEvents === 'auto') {
      return;
    }
    ref.style.pointerEvents = 'auto';
  });

  touchRefs.value?.forEach((ref) => {
    if (ref.style.pointerEvents === 'none') {
      return;
    }
    ref.style.pointerEvents = 'none';
  });
};

const handleHoverEvent = (event: MouseEvent) => {
  if (!zoomer.value) {
    return;
  }

  if (scale.value !== 2) {
    scale.value = 2;
  }

  zoomer.value[activeIndex.value]?.setData({
    originX: event.layerX,
    originY: event.layerY,
  });
};

const handleMouseLeaveEvent = () => {
  if (!zoomer.value) {
    return;
  }

  if (scale.value !== 1) {
    scale.value = 1;
  }
};

const handleWheelEvent = (event: WheelEvent) => {
  scrollBy({ top: event.deltaY, behavior: 'auto' });
};

watch(() => activeIndex.value, () => {
  scale.value = 1;
});

onUnmounted(() => {
  scale.value = 1;
});
</script>

<style lang="scss">
@screen mobile {
  .mobile-thumbnail {
    margin-top: 10px;
    width: auto;
    flex-direction: row;
    gap: 4px;

    button {
      width: 10px;
      height: 10px;
      background: #ccd1d5;
      border-radius: 50px;
      margin: 0;

      &.border-primary-700 {
        background: #e2001a;
      }

      img {
        display: none;
      }
    }
  }
}
</style>
